import { useMemo } from "react";
import { DollarSign, TrendingUp, RotateCcw, Percent, Users } from "lucide-react";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, Line } from "recharts";
import { SummaryCard } from "./SummaryCard";
import type { ChartResponse } from "../../types";

interface SubscriptionsSummary {
  active_subscriptions: number;
  active_free_trials: number;
  new_subscriptions: number;
  cancelled_subscriptions: number;
  trial_conversions: number;
  churn: number | string;
  mrr: number | string;
  actual_revenue: number | string;
  gross_revenue: number | string;
}

interface SubscriptionsTabContentProps {
  loadingSummary: boolean;
  subscriptionsSummary?: SubscriptionsSummary;
  loadingSubscriptionsTimeseries: boolean;
  subscriptionsTimeseries?: ChartResponse;
}

export function SubscriptionsTabContent({
  loadingSummary,
  subscriptionsSummary,
  loadingSubscriptionsTimeseries,
  subscriptionsTimeseries,
}: SubscriptionsTabContentProps) {
  const retentionRate = useMemo(() => {
    if (!subscriptionsSummary?.active_subscriptions || !subscriptionsSummary?.cancelled_subscriptions) return 0;
    const total = subscriptionsSummary.active_subscriptions + subscriptionsSummary.cancelled_subscriptions;
    if (total === 0) return 0;
    return (subscriptionsSummary.active_subscriptions / total) * 100;
  }, [subscriptionsSummary]);

  const growthRate = useMemo(() => {
    if (!subscriptionsSummary?.new_subscriptions || !subscriptionsSummary?.cancelled_subscriptions) return 0;
    const netGrowth = subscriptionsSummary.new_subscriptions - subscriptionsSummary.cancelled_subscriptions;
    if (!subscriptionsSummary.active_subscriptions) return 0;
    return (netGrowth / subscriptionsSummary.active_subscriptions) * 100;
  }, [subscriptionsSummary]);

  const trialConversionRate = useMemo(() => {
    if (!subscriptionsSummary?.active_free_trials || !subscriptionsSummary?.trial_conversions) return 0;
    if (subscriptionsSummary.active_free_trials === 0) return 0;
    return (subscriptionsSummary.trial_conversions / subscriptionsSummary.active_free_trials) * 100;
  }, [subscriptionsSummary]);

  const churnValue = useMemo(() => Number(subscriptionsSummary?.churn ?? 0), [subscriptionsSummary]);

  const averageRevenuePerUser = useMemo(() => {
    if (!subscriptionsSummary?.active_subscriptions || !subscriptionsSummary?.mrr) return 0;
    const total = Number(subscriptionsSummary.mrr) || 0;
    if (subscriptionsSummary.active_subscriptions === 0) return 0;
    return total / subscriptionsSummary.active_subscriptions;
  }, [subscriptionsSummary]);

  const subscriptionsChartData = useMemo(() => {
    if (!subscriptionsTimeseries?.labels || !subscriptionsTimeseries?.series) return [];

    const activeSeries = subscriptionsTimeseries.series.find((s) => s.name === "Active Subscriptions");
    const newSeries = subscriptionsTimeseries.series.find((s) => s.name === "New Subscriptions");
    const cancelledSeries = subscriptionsTimeseries.series.find((s) => s.name === "Cancelled Subscriptions");

    return subscriptionsTimeseries.labels.map((label, index) => ({
      date: new Date(label).toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
      }),
      active: activeSeries?.values[index] || 0,
      new: newSeries?.values[index] || 0,
      cancelled: cancelledSeries?.values[index] || 0,
    }));
  }, [subscriptionsTimeseries]);


  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SummaryCard
          title="Active Subscriptions"
          value={subscriptionsSummary?.active_subscriptions ?? 0}
          loading={loadingSummary}
          format="number"
          icon={TrendingUp}
          color="bg-[#22C55E33]"
          tooltip="Total de assinaturas ativas no período. Inclui assinaturas pagas e com desconto."
        />
        <SummaryCard
          title="Active Free Trials"
          value={subscriptionsSummary?.active_free_trials ?? 0}
          loading={loadingSummary}
          format="number"
          icon={Percent}
          color="bg-[#3B82F633]"
          platform="apple"
          tooltip="Total de testes gratuitos ativos no período."
        />
        <SummaryCard
          title="New Subscriptions"
          value={subscriptionsSummary?.new_subscriptions ?? 0}
          loading={loadingSummary}
          format="number"
          icon={DollarSign}
          color="bg-[#8B5CF633]"
          tooltip="Novas assinaturas iniciadas por clientes no período."
        />
        <SummaryCard
          title="Cancelled Subscriptions"
          value={subscriptionsSummary?.cancelled_subscriptions ?? 0}
          loading={loadingSummary}
          format="number"
          icon={RotateCcw}
          color="bg-[#EF444433]"
          tooltip="Assinaturas canceladas pelos clientes no período."
        />
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Performance e Saúde das Assinaturas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SummaryCard
            title="Retention Rate"
            value={retentionRate}
            loading={loadingSummary}
            format="percentage"
            icon={TrendingUp}
            color="bg-[#22C55E33]"
            tooltip="Taxa de retenção de assinantes. Quanto maior, melhor. (Active / (Active + Cancelled) * 100)"
          />
          <SummaryCard
            title="Growth Rate"
            value={growthRate}
            loading={loadingSummary}
            format="percentage"
            icon={TrendingUp}
            color="bg-[#3B82F633]"
            tooltip="Taxa de crescimento líquido das assinaturas. ((New - Cancelled) / Active * 100)"
          />
          <SummaryCard
            title="Trial Conversion Rate"
            value={trialConversionRate}
            loading={loadingSummary}
            format="percentage"
            icon={Percent}
            color="bg-[#8B5CF633]"
            platform="apple"
            tooltip="Percentual de trials que se converteram em assinaturas pagas. Indica eficácia do modelo freemium."
          />
          <SummaryCard
            title="Churn Rate"
            value={churnValue}
            loading={loadingSummary}
            format="percentage"
            icon={RotateCcw}
            color="bg-[#EF444433]"
            platform="apple"
            tooltip="Percentual de cancelamentos sobre o total de assinaturas ativas. Valores baixos são ideais."
          />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Receita das Assinaturas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SummaryCard
            title="MRR"
            value={Number(subscriptionsSummary?.mrr ?? 0)}
            loading={loadingSummary}
            format="currency"
            icon={TrendingUp}
            color="bg-[#3B82F633]"
            platform="apple"
            tooltip="Receita recorrente mensal das assinaturas. Métrica chave para SaaS."
          />
          <SummaryCard
            title="Net Revenue"
            value={Number(subscriptionsSummary?.actual_revenue ?? 0)}
            loading={loadingSummary}
            format="currency"
            icon={DollarSign}
            color="bg-[#22C55E33]"
            platform="apple"
            tooltip="Receita líquida das assinaturas após taxas das lojas. Valor que você realmente recebe."
          />
          <SummaryCard
            title="Gross Revenue"
            value={Number(subscriptionsSummary?.gross_revenue ?? 0)}
            loading={loadingSummary}
            format="currency"
            icon={TrendingUp}
            color="bg-[#8B5CF633]"
            platform="apple"
            tooltip="Receita bruta das assinaturas antes das taxas das lojas. Valor total gerado."
          />
          <SummaryCard
            title="Avg Revenue per User"
            value={averageRevenuePerUser}
            loading={loadingSummary}
            format="currency"
            icon={DollarSign}
            color="bg-[#F59E0B33]"
            platform="apple"
            tooltip="Receita média por usuário assinante. Indica o valor médio das assinaturas."
          />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Evolução das Assinaturas</h2>
        <div className="bg-[#232323] border-none rounded-lg">
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" />
                Active, New e Cancelled Subscriptions
              </h3>
              <p className="text-sm text-white/60">Acompanhe o crescimento e comportamento das assinaturas ao longo do tempo</p>
            </div>
            <div style={{ width: "100%", height: 400 }}>
              {loadingSubscriptionsTimeseries ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Carregando dados...</p>
                </div>
              ) : subscriptionsChartData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Sem dados disponíveis para o período selecionado</p>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={subscriptionsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "#ffffff80" }} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "#232323",
                        border: "1px solid #ffffff20",
                        borderRadius: "8px",
                        color: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                      formatter={(value: number, name: string) => [
                        new Intl.NumberFormat("pt-BR").format(value),
                        name === "active" ? "Ativas" : name === "new" ? "Novas" : "Canceladas",
                      ]}
                    />
                    <Legend
                      wrapperStyle={{ color: "#ffffff80", fontSize: "12px" }}
                      formatter={(value: string) =>
                        value === "active" ? "Assinaturas Ativas" : value === "new" ? "Novas Assinaturas" : "Canceladas"
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="active"
                      stroke="#22C55E"
                      strokeWidth={3}
                      dot={{ fill: "#22C55E", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#22C55E", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="new"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: "#3B82F6", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cancelled"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={{ fill: "#EF4444", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: "#EF4444", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
